/**
 * 房源配置（房源类型 / 经营属性 / 状态说明）的运行时读取。
 *
 * 这三类取值原先写死在前端常量里，现在由「房源配置」页维护，存在平台字典中。
 * 页面渲染必须读到最新配置，所以统一走这里：
 *
 * - `labelOf` 展示用：字典（含停用项）-> 内置文案 -> 原始取值，三级回退，
 *   保证用户自建或已停用的取值都不会渲染成空白；
 * - `optionsOf` 选择用：只给启用中的项；配置尚未加载或加载失败时回退内置项，
 *   避免下拉全空导致建不了房源；已加载但确实为空时保持为空（尊重用户把项都停用）。
 */
import React from 'react';
import { parkApi } from '@/services/bff/park.api';
import type {
  SpaceConfigGroup,
  SpaceConfigItem,
  SpaceConfigKind,
} from '@/services/bff/types/park';
import { operationModeText, spaceTypeText, statusText } from './spaceLabels';

const BUILTIN_TEXT: Record<SpaceConfigKind, Record<string, string>> = {
  space_type: spaceTypeText,
  operation_mode: operationModeText,
  space_status: statusText,
};

const toOptions = (items: SpaceConfigItem[]) =>
  items.map((item) => ({ value: item.value, label: item.label }));

const builtinOptions = (kind: SpaceConfigKind) =>
  Object.entries(BUILTIN_TEXT[kind]).map(([value, label]) => ({
    value,
    label,
  }));

export type SpaceConfigLookup = {
  groups: SpaceConfigGroup[];
  /** 配置是否已成功加载（用于区分「还没拿到」与「确实为空」） */
  loaded: boolean;
  optionsOf: (kind: SpaceConfigKind) => { value: string; label: string }[];
  labelOf: (kind: SpaceConfigKind, code?: string | null) => string;
  reload: () => Promise<void>;
};

/**
 * 读取房源配置。只读：不会创建字典（写路径只有「房源配置」页的接口）。
 * 读取失败时 loaded=false，各页面仍能用内置文案与内置选项正常工作。
 */
export const useSpaceConfig = (): SpaceConfigLookup => {
  const [groups, setGroups] = React.useState<SpaceConfigGroup[]>([]);
  const [loaded, setLoaded] = React.useState(false);

  const reload = React.useCallback(async () => {
    try {
      const result = await parkApi.getSpaceConfig();
      setGroups(result || []);
      setLoaded(true);
    } catch {
      // 读取失败不阻断宿主页面：保留内置回退。
      setGroups([]);
      setLoaded(false);
    }
  }, []);

  const optionsOf = React.useCallback(
    (kind: SpaceConfigKind) => {
      const group = groups.find((item) => item.kind === kind);
      const active = (group?.items || []).filter(
        (item) => item.status === 'active',
      );
      if (active.length) return toOptions(active);
      // 还没拿到配置（含请求失败）才回退内置项；加载成功但为空则如实返回空。
      return loaded ? [] : builtinOptions(kind);
    },
    [groups, loaded],
  );

  const labelOf = React.useCallback(
    (kind: SpaceConfigKind, code?: string | null) => {
      if (!code) return '-';
      const found = groups
        .find((group) => group.kind === kind)
        ?.items.find((item) => item.value === code);
      if (found) return found.label;
      return BUILTIN_TEXT[kind][code] ?? code;
    },
    [groups],
  );

  return { groups, loaded, optionsOf, labelOf, reload };
};

/** 配置项的启用状态文案 */
export const spaceConfigStatusText: Record<string, string> = {
  active: '启用',
  inactive: '停用',
};
